import { useState, useEffect } from "react";
import { toast } from "sonner";
import axios from "axios";
import { Save, Eye, EyeOff, RotateCcw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("dynamo_token")}` });

const PLACEHOLDERS = [
  { key: "{{User First Name}}",       desc: "Adult/parent first name", highlight: true },
  { key: "{{Beneficiary First Name}}", desc: "Child's first name", highlight: true },
  { key: "{{accept_url}}",            desc: "Accept taster button link (auto-filled)", highlight: true },
  { key: "{{decline_url}}",           desc: "Decline taster button link (auto-filled)", highlight: true },
  { key: "{{class_day}}",         desc: "Day of the week (e.g. Tuesday)" },
  { key: "{{taster_date}}",       desc: "Taster date with year (e.g. 11th August 2026)" },
  { key: "{{taster_date_short}}", desc: "Taster date, no year (e.g. 11th August)" },
  { key: "{{taster_time}}",       desc: "Session start time (e.g. 4:00pm)" },
  { key: "{{start_date}}",        desc: "Start date with year (e.g. 18th August 2026)" },
  { key: "{{start_date_short}}",  desc: "Start date, no year (e.g. 18th August)" },
  { key: "{{class_price}}",       desc: "Taster price (e.g. 10.00)" },
  { key: "{{class_duration}}",    desc: "Session length (e.g. 1 hour)" },
  { key: "{{class_name}}",        desc: "Class type (e.g. Recreational)" },
];

export default function EmailTemplatesTab() {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => { fetchTemplate(); }, []);

  async function fetchTemplate() {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/email-templates/taster_invite`, { headers: auth() });
      setSubject(data.subject || "");
      setBody(data.body || "");
    } catch {
      toast.error("Failed to load template");
    } finally {
      setLoading(false);
    }
  }

  async function handleSave() {
    setSaving(true);
    try {
      await axios.put(`${API}/email-templates/taster_invite`, { subject, body }, { headers: auth() });
      toast.success("Template saved");
    } catch {
      toast.error("Failed to save template");
    } finally {
      setSaving(false);
    }
  }

  async function handleReset() {
    if (!window.confirm("Reset to the default template? Your current template will be replaced.")) return;
    setResetting(true);
    try {
      const { data } = await axios.delete(`${API}/email-templates/taster_invite`, { headers: auth() });
      setSubject(data.subject || "");
      setBody(data.body || "");
      toast.success("Template reset to default");
    } catch {
      toast.error("Failed to reset template");
    } finally {
      setResetting(false);
    }
  }

  function insertPlaceholder(key) {
    setBody(b => b + key);
  }

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
        <div>
          <h2 className="text-base font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
            Email Templates
          </h2>
          <p className="text-xs text-slate-400 mt-0.5">Taster session invite template</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="reset-template-button"
            onClick={handleReset}
            disabled={resetting || loading}
            className="flex items-center gap-1.5 text-slate-500 hover:text-red-600
                       text-sm px-3 py-2 rounded-md border border-slate-200 hover:border-red-300 transition-colors disabled:opacity-50"
          >
            <RotateCcw size={14} />
            {resetting ? "Resetting…" : "Reset"}
          </button>
          <button
            data-testid="toggle-preview-button"
            onClick={() => setShowPreview(p => !p)}
            className="flex items-center gap-1.5 text-slate-500 hover:text-slate-700
                       text-sm px-3 py-2 rounded-md border border-slate-200 hover:border-slate-300 transition-colors"
          >
            {showPreview ? <EyeOff size={14} /> : <Eye size={14} />}
            {showPreview ? "Edit" : "Preview"}
          </button>
          <button
            data-testid="save-template-button"
            onClick={handleSave}
            disabled={saving || loading}
            className="flex items-center gap-1.5 bg-[#1656a4] hover:bg-[#124687]
                       text-white text-sm font-medium px-3.5 py-2 rounded-md transition-colors disabled:opacity-50"
          >
            <Save size={14} />
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      <div className="p-5 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: placeholders reference */}
        <div className="lg:col-span-1">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Placeholders</p>
          <div className="space-y-1.5">
            {PLACEHOLDERS.map(p => (
              <button
                key={p.key}
                data-testid={`placeholder-${p.key}`}
                onClick={() => insertPlaceholder(p.key)}
                title="Click to insert"
                className={`w-full text-left group px-3 py-2 rounded-md
                           border transition-colors
                           ${p.highlight
                             ? "bg-blue-50 border-blue-200 hover:bg-blue-100 hover:border-blue-300"
                             : "bg-slate-50 border-slate-100 hover:bg-blue-50 hover:border-blue-200"}`}
              >
                <p className="text-xs font-mono text-[#1656a4]">{p.key}</p>
                <p className="text-xs text-slate-400 mt-0.5">{p.desc}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Right: editor / preview */}
        <div className="lg:col-span-2 space-y-4">
          {loading ? (
            <div className="space-y-3">
              <div className="h-10 bg-slate-100 rounded animate-pulse" />
              <div className="h-64 bg-slate-100 rounded animate-pulse" />
            </div>
          ) : showPreview ? (
            <div>
              <div className="mb-3 px-3 py-2 bg-slate-50 rounded-lg border border-slate-200">
                <p className="text-xs text-slate-400 mb-0.5">Subject</p>
                <p className="text-sm font-medium text-slate-800">{subject}</p>
              </div>
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <div className="bg-slate-50 px-3 py-1.5 border-b border-slate-200">
                  <p className="text-xs text-slate-400">Body preview (with raw placeholders)</p>
                </div>
                <div
                  data-testid="template-preview"
                  className="p-4 overflow-y-auto max-h-96 text-sm"
                  dangerouslySetInnerHTML={{ __html: body }}
                />
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">Subject line</label>
                <input
                  data-testid="template-subject-input"
                  type="text"
                  value={subject}
                  onChange={e => setSubject(e.target.value)}
                  placeholder="e.g. Offer of a taster session for {{child_first_name}}"
                  className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-md
                             focus:outline-none focus:ring-2 focus:ring-[#1656a4] focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">
                  Email body
                  <span className="ml-1.5 font-normal text-slate-400">(HTML supported)</span>
                </label>
                <textarea
                  data-testid="template-body-textarea"
                  value={body}
                  onChange={e => setBody(e.target.value)}
                  rows={16}
                  placeholder="Paste your email HTML here, or type plain text. Use placeholders from the left panel."
                  className="w-full px-3 py-2.5 text-sm font-mono border border-slate-200 rounded-md resize-y
                             focus:outline-none focus:ring-2 focus:ring-[#1656a4] focus:border-transparent"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
